import { Tooltip } from '@mui/material'
import { LatestQueueItem } from 'api/optJobs/optJobs.api'
import { t } from 'i18next'
import React, { ReactElement } from 'react'
import PublishRocketIcon, { PublishRocketStatus } from '../../PublishRocketIcon/PublishRocketIcon'
import styles from '../LatestPublishedOptimizationInfoBox.module.less'
import { useLatestQueueItemsInfo, useTimeSincePublished } from '../latestPublishedInfoBoxHooks'

type PublishedOptimizationWarningBoxProps = {
  isVisible: boolean
}

export default function PublishedOptimizationWarningBox({
  isVisible,
}: PublishedOptimizationWarningBoxProps): ReactElement {
  const { subtypes, latestQueueItemData, status: latestQueueItemStatus } = useLatestQueueItemsInfo({ type: 'regular' })

  if (!isVisible || latestQueueItemStatus === 'pending') {
    return <></>
  }

  if (subtypes.length > 0) {
    return (
      <div className={styles.LatestPublishedOptimizationInfoBox_Container}>
        {subtypes.map((subtype) => {
          const queueItemForSubtype = latestQueueItemData?.find((item) => item.subtype === subtype.subtype)
          return (
            <div key={subtype.subtype}>
              <PublishedOptimizationItem
                subtype={subtype}
                latestQueueItem={queueItemForSubtype ?? null}
                noPublishedFound={!queueItemForSubtype}
              />
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <div className={styles.LatestPublishedOptimizationInfoBox_Container}>
      <PublishedOptimizationItem
        latestQueueItem={latestQueueItemData?.[0] ?? null}
        noPublishedFound={!latestQueueItemData}
      />
    </div>
  )
}

type PublishedOptimizationItemProps = {
  latestQueueItem: LatestQueueItem | null
  subtype?: Subtype
  noPublishedFound?: boolean
}

function PublishedOptimizationItem({
  latestQueueItem,
  subtype,
  noPublishedFound,
}: PublishedOptimizationItemProps): ReactElement {
  const timeSincePublished = useTimeSincePublished({ latestQueueItem })
  const hoursSinceLastPublished = !timeSincePublished
    ? NaN
    : !timeSincePublished.includes('h')
      ? 0
      : parseInt(timeSincePublished.split('h')[0])

  const WARNING_THRESHOLD_HOURS = 2
  const DANGER_THRESHOLD_HOURS = 3

  const warning = hoursSinceLastPublished >= WARNING_THRESHOLD_HOURS
  const danger = hoursSinceLastPublished >= DANGER_THRESHOLD_HOURS

  let status: PublishRocketStatus = 'active'
  if (noPublishedFound) {
    status = 'inactive'
  } else if (danger) {
    status = 'danger'
  } else if (warning) {
    status = 'warning'
  }

  function explainingTooltip(timeAgo: string | null, subtype?: Subtype): string {
    if (noPublishedFound) {
      return t('No production plan has been published')
    }

    if (warning || danger) {
      return t('{{subtype}} has not been published in over {{numberOfHours}} ({{timeSincePublished}})', {
        subtype: subtype?.subtype_display_name ?? t('Production plan'),
        numberOfHours: danger ? '3h' : '2h',
        timeSincePublished: timeAgo,
      })
    }

    return t('{{subtype}} was last published {{timeSincePublished}} ago', {
      subtype: subtype?.subtype_display_name ?? t('Production plan'),
      timeSincePublished: timeAgo,
    })
  }

  const infoText = subtype?.subtype_display_name ?? timeSincePublished ?? t('No production plan has been published')

  return (
    <Tooltip title={explainingTooltip(timeSincePublished, subtype)} arrow>
      <div className={styles.LatestPublishedOptimizationInfoBox_Item}>
        <PublishRocketIcon status={status} />
        {infoText}
      </div>
    </Tooltip>
  )
}
